import type {
  BrandCheck,
  BrandSetCheck,
  FilterCheck,
  SegmentCheck,
} from "@/interfaces/selections/selections.types";
import { formatNameList } from "./formatNameList";

export interface BlockedDescription {
  /** Short status shown in the row's right rail. */
  label: string;
  /** Optional sentence beneath the name. */
  detail?: string;
  /** True when a customer-success rep could resolve it; drives the support line. */
  entitlement: boolean;
}

const NOT_LICENSED = "Not licensed";
const GONE = "No longer available";
const UNKNOWN = "Not available";

/**
 * Subject-verb agreement is computed from the raw array, not the formatted
 * string, because the formatter collapses four or more names into a phrase
 * whose head reads singular ("Oracle, Adobe and 3 others").
 */
const contractSentence = (names: string[] | undefined): string | undefined => {
  const list = formatNameList(names);
  if (!list) return undefined;
  const count = (names ?? []).filter((n) => n.trim() !== "").length;
  return count === 1 ? `${list} isn't on your contract.` : `${list} aren't on your contract.`;
};

export function describeBlockedBrandSet(check: BrandSetCheck): BlockedDescription {
  const reason = check.reason;
  if (!reason) return { label: UNKNOWN, entitlement: false };
  switch (reason) {
    case "MEMBERS_NOT_ACCESSIBLE":
      return {
        label: NOT_LICENSED,
        detail:
          contractSentence(check.inaccessibleMemberNames) ??
          "Some brands in this set aren't on your contract.",
        entitlement: true,
      };
    case "NOT_FOUND":
    case "INVALID_ID":
      return { label: GONE, detail: "This set no longer exists.", entitlement: false };
    default: {
      const exhaustive: never = reason;
      void exhaustive;
      return { label: UNKNOWN, entitlement: false };
    }
  }
}

export function describeBlockedSegment(check: SegmentCheck): BlockedDescription {
  const reason = check.reason;
  if (!reason) return { label: UNKNOWN, entitlement: false };
  switch (reason) {
    case "FILTERS_NOT_ACCESSIBLE":
      return {
        label: NOT_LICENSED,
        detail:
          contractSentence(check.inaccessibleFilterLabels) ??
          "This segment uses filters that aren't on your contract.",
        entitlement: true,
      };
    case "NOT_APPLICABLE_IN_YOUR_MARKETS":
      return {
        label: "Not in your markets",
        detail: "This segment doesn't apply to the markets you have.",
        entitlement: true,
      };
    case "DEACTIVATED":
      return {
        label: "No longer active",
        detail: "This segment has been deactivated.",
        entitlement: false,
      };
    // The response carries no replacement label, so the detail stays generic.
    // Naming a successor here would mean inventing one.
    case "SUPERSEDED":
      return { label: "Superseded", detail: "Replaced by a newer version.", entitlement: false };
    case "NOT_FOUND":
    case "INVALID_ID":
      return { label: GONE, detail: "This segment no longer exists.", entitlement: false };
    default: {
      const exhaustive: never = reason;
      void exhaustive;
      return { label: UNKNOWN, entitlement: false };
    }
  }
}

// A brand carries no detail beyond accessible: false — it is either licensed
// or it isn't, so the parameter is unused by design.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function describeBlockedBrand(_check: BrandCheck): BlockedDescription {
  return { label: NOT_LICENSED, entitlement: true };
}

/**
 * A filter blocks either wholesale or because specific options are out of
 * reach. Naming the options follows the same rule as brand-set members: say
 * what the data supports, and stay generic when it supports nothing.
 */
export function describeBlockedFilter(check: FilterCheck): BlockedDescription {
  const inaccessible = (check.options ?? [])
    .filter((o) => !o.accessible)
    .map((o) => o.label ?? o.code);
  return {
    label: NOT_LICENSED,
    detail: contractSentence(inaccessible),
    entitlement: true,
  };
}
